import { useCallback, useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";

// Lote 76 — sincronização em segundo plano. O servidor roda a busca por conta própria; esta
// tela só acompanha o estado (GET /publications/sync/status). Assim, sair da aba, trocar de
// app ou bloquear o iPhone não interrompe nada: ao voltar, o hook reencontra o job rodando
// (ou já concluído) e mostra o resultado uma única vez.
//
// kind: "djen"    = busca completa (DJEN + DataJud)      -> POST /publications/sync
//       "datajud" = só os processos cadastrados (DataJud) -> POST /publications/sync-datajud
const START_URL = { djen: "/publications/sync", datajud: "/publications/sync-datajud" };
const POLL_MS = 2500;
const FRESH_MS = 3 * 60 * 1000; // resultado "novo" para avisar ao voltar à tela

const seenKey = (kind) => `iapauta_sync_seen_${kind}`;
const getSeen = (kind) => { try { return localStorage.getItem(seenKey(kind)); } catch { return null; } };
const setSeen = (kind, id) => { try { localStorage.setItem(seenKey(kind), id); } catch { /* sem storage: ok */ } };

export default function useSyncJob(kind, onFinished) {
  const [running, setRunning] = useState(false);
  const timer = useRef(null);
  const finishedRef = useRef(onFinished);
  finishedRef.current = onFinished;

  const stop = () => { if (timer.current) { clearInterval(timer.current); timer.current = null; } };

  const check = useCallback(async ({ announceFresh = false } = {}) => {
    try {
      const { data } = await http.get("/publications/sync/status");
      const job = data?.[kind];
      if (!job) { setRunning(false); stop(); return null; }
      if (job.status === "running") {
        setRunning(true);
        if (!timer.current) timer.current = setInterval(() => check(), POLL_MS);
        return job;
      }
      setRunning(false);
      stop();
      const finishedAt = job.finished_at ? new Date(job.finished_at).getTime() : 0;
      const fresh = Date.now() - finishedAt < FRESH_MS;
      if (getSeen(kind) !== job.job_id && (!announceFresh || fresh)) {
        setSeen(kind, job.job_id);
        finishedRef.current?.(job);
      }
      return job;
    } catch {
      return null; // falha de rede momentânea: o próximo ciclo tenta de novo
    }
  }, [kind]);

  // Ao abrir a tela: já existe uma busca rodando (ou acabou há pouco)? Reencontra.
  useEffect(() => {
    check({ announceFresh: true });
    const onVisible = () => { if (document.visibilityState === "visible") check({ announceFresh: true }); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { document.removeEventListener("visibilitychange", onVisible); stop(); };
  }, [check]);

  const start = async () => {
    try {
      const { data } = await http.post(START_URL[kind]);
      if (data && data.started === false) {
        toast.info("Já existe uma busca em andamento", { description: "Ela continua mesmo que você saia desta tela." });
      }
      setRunning(true);
      if (!timer.current) timer.current = setInterval(() => check(), POLL_MS);
      check();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao iniciar a busca");
    }
  };

  return { running, start };
}
